import {useEffect, useState} from "react";

import {Card, Checkbox, Col, Collapse, App as AntdApp, Row, Switch, Select, Button} from 'antd';

import api from "../../../api.js";

import {getLabel} from "../../additional-data/lib/labelMapper.js";
import styles from "./UserPreferences.module.css";
import {getEquipmentTabOptions, getMaterialTabOptions} from "@/pages/additional-data/lib/TabDefinitions.js";
import {StockOnHand} from '@/variants/index.js';

const DEFAULT_MATERIAL_TAB_SETTING = "Default Material Tab";
const DEFAULT_EQUIPMENT_TAB_SETTING = "Default Equipment Tab";
const DEFAULT_OPERATORS_SETTING = "Default Operators";

function filterSettingsColumns (columns) {
    if (!columns) return columns;
    const hidden = new Set(StockOnHand.getHiddenSettingsColumns());
    return columns.filter(col => !hidden.has(col));
}

export const UserPreferences = () => {
	const [tables, setTables] = useState([]);
	const [defaultColumns, setDefaultColumns] = useState({});
	const [overriddenColumns, setOverriddenColumns] = useState({});
	const [activeKey, setActiveKey] = useState([]);

	const [switchState, setSwitchState] = useState(false);
	const [defaultTab, setDefaultTab] = useState(null);
	const [defaultEquipmentTab, setDefaultEquipmentTab] = useState(null);
	const [defaultOperators, setDefaultOperators] = useState([]);
	const [availableOperators, setAvailableOperators] = useState([]);

	const {message, modal} = AntdApp.useApp();

    function loadSpecialSettings() {
        const requests = [
            api.get(`/settings/table-overridden?name=${DEFAULT_MATERIAL_TAB_SETTING}`, { omitGlobalError: true }),
            api.get(`/settings/table-overridden?name=${DEFAULT_EQUIPMENT_TAB_SETTING}`, { omitGlobalError: true }),
            api.get(`/settings/table-overridden?name=${DEFAULT_OPERATORS_SETTING}`, { omitGlobalError: true }),
        ];

        return Promise.allSettled(requests).then(([matTabRes, equipTabRes, opsSettingsRes]) => {
            if (matTabRes.status === "fulfilled") {
                const data = matTabRes.value.data;
                setDefaultTab(data && data.length > 0 ? data[0] : null);
            } else {
                console.log(matTabRes.reason);
                message.error("Failed to load material tab settings");
            }

            if (equipTabRes.status === "fulfilled") {
                const data = equipTabRes.value.data;
                setDefaultEquipmentTab(data && data.length > 0 ? data[0] : null);
            } else {
                console.log(equipTabRes.reason);
                message.error("Failed to load equipment tab settings");
            }

            if (opsSettingsRes.status === "fulfilled") {
                const data = opsSettingsRes.value.data;
                setDefaultOperators(data && data.length > 0 ? data : []);
            } else {
                console.log(opsSettingsRes.reason);
                message.error("Failed to load operator settings");
            }
        });
    }

	useEffect(() => {
		const requests = [
			api.get("/settings/available-tables", { omitGlobalError: true }),
			api.get(`/operators?global=true`, { omitGlobalError: true }),
            loadSpecialSettings()
		];

		Promise.allSettled(requests).then(([tablesRes, allOpsRes]) => {
			if (tablesRes.status === "fulfilled") {
				setTables(tablesRes.value.data);
			} else {
				console.log(tablesRes.reason);
				message.error("Failed to load tables list");
			}

			if (allOpsRes.status === "fulfilled") {
				setAvailableOperators(allOpsRes.value.data);
			} else {
				console.log(allOpsRes.reason);
				message.error("Failed to load operators directory");
			}
		});
	}, []);

	function getDefaultColumns(tableName) {
		api.get(`/settings/table-default?name=${tableName}`)
			.then(json => setDefaultColumns(state => ({...state, [tableName]: json.data})))
			.catch(err => console.log(err));
	}

	function getOverriddenColumns(tableName) {
		api.get(`/settings/table-overridden?name=${tableName}`)
			.then(json => {
				setOverriddenColumns(state => ({...state, [tableName]: json.data}));
				if (tableName === "Description") {
					setSwitchState(json.data[0] === "short");
				}
			})
			.catch(error => {
				setOverriddenColumns(state => ({...state, [tableName]: null}));
				console.log(error);
			});
	}

	function getChecked(tableName) {
		if (overriddenColumns[tableName] && overriddenColumns[tableName].length > 1) {
			return filterSettingsColumns(overriddenColumns[tableName]);
		} else if (defaultColumns[tableName]) {
			return filterSettingsColumns(defaultColumns[tableName]);
		}
	}

    function reloadTablePreferences(tableList) {
        for (const table of tableList) {
            getDefaultColumns(table.name);
            getOverriddenColumns(table.name);
        }
        getDefaultColumns("Description");
        getOverriddenColumns("Description");
    }

	useEffect(() => {
        if (tables.length > 0) {
            reloadTablePreferences(tables);
        }
	}, [tables, reloadTablePreferences]);

	function onChange(checked, tableName) {
		api.post(`/settings/override-columns?name=${tableName}`, {
			columns: [...checked, "label"],
		}).then(json => {
			message.success("Updated");
			setOverriddenColumns({
				...overriddenColumns,
				[tableName]: json.data,
			});
		});
	}

	function saveValue(state) {
		api.post(`/settings/override-columns?name=Description`, {
			columns: [state ? "short" : "long"],
		}, { omitGlobalError: true })
			.then(json => {
				message.success("Updated");
				setSwitchState(state);
			})
			.catch(error => {
				setSwitchState(!state);
				console.error("Save value error:", error);
				message.error("Failed to update");
			});
	}

	function saveDefaultTab(tabKey) {
		api.post(`/settings/override-columns?name=${DEFAULT_MATERIAL_TAB_SETTING}`, {
			columns: [tabKey],
		}, { omitGlobalError: true })
			.then(() => {
				message.success("Updated");
				setDefaultTab(tabKey);
			})
			.catch(error => {
				console.error("Save default tab error:", error);
				message.error("Failed to update");
			});
	}

	function saveDefaultEquipmentTab(tabKey) {
		api.post(`/settings/override-columns?name=${DEFAULT_EQUIPMENT_TAB_SETTING}`, {
			columns: [tabKey],
		}, { omitGlobalError: true })
			.then(() => {
				message.success("Updated");
				setDefaultEquipmentTab(tabKey);
			})
			.catch(error => {
				console.error("Save default equipment tab error:", error);
				message.error("Failed to update");
			});
	}

	function saveDefaultOperators(values) {
		let selected = [...values];

		if (selected.includes("none")) {
			selected = [];
		} else if (selected.includes("all")) {
			selected = [];
			availableOperators.forEach(hub => {
				if (hub.operators) {
					hub.operators.forEach(op => {
						if (!selected.includes(op)) selected.push(op);
					});
				}
			});
		}

		api.post(`/settings/override-columns?name=${DEFAULT_OPERATORS_SETTING}`, {
			columns: selected,
		}, { omitGlobalError: true })
			.then(() => {
				message.success("Updated");
				setDefaultOperators(selected);
			})
			.catch(error => {
				console.error("Save default operators error:", error);
				message.error("Failed to update");
			});
	}

	function getOperatorOptions() {
		let items = [
			{label: "Select all", value: "all", key: 0},
			{label: "Deselect all", value: "none", key: 1},
		];
		for (let i = 0; i < availableOperators.length; i++) {
			const hub = availableOperators[i];
			const optGroup = {key: hub.name, label: hub.name, options: []};
			if (hub.operators) {
				for (let j = 0; j < hub.operators.length; j++) {
					optGroup.options.push({key: hub.operators[j], value: hub.operators[j], label: hub.operators[j]});
				}
			}
			items.push(optGroup);
		}
		return items;
	}

    function handleResetToDefaults() {
        modal.confirm({
                          title: "Reset to defaults",
                          content: "Are you sure you want to reset all user preferences to their default values? This action cannot be undone.",
                          okText: "Reset",
                          okType: "danger",
                          cancelText: "Cancel",
                          onOk: async () => {
                              try {
                                  await api.delete("/settings/overrides", { omitGlobalError: true });

                                  message.success("Preferences reset to defaults");

                                  setOverriddenColumns({});
                                  setSwitchState(false);
                                  setDefaultTab(null);
                                  setDefaultEquipmentTab(null);
                                  setDefaultOperators([]);
                                  reloadTablePreferences(tables);
                                  await loadSpecialSettings();

                              } catch (error) {
                                  message.error("Failed to reset preferences");
                                  throw error;
                              }
                          },
                      });
    }

	const tableItems = tables.map((v, i) => ({
		key: (i + 1).toString(),
		label: v.name,
		children: (
			<Checkbox.Group
				value={getChecked(v.name)}
				className={styles.checkboxGroup}
				onChange={checked => onChange(checked, v.name)}>
				<Row>
					{defaultColumns[v.name] &&
						filterSettingsColumns(defaultColumns[v.name]).map(col => {
							if (col !== "label") {
								return (
									<Col span={8} key={col}>
										<Checkbox value={col}>{getLabel(col)}</Checkbox>
									</Col>
								);
							} else return null;
						})}
				</Row>
			</Checkbox.Group>
		),
	}));

	const defaultItems = [
		{
			key: "default-material-tab",
			label: "Default Material Tab",
			children: (
				<Select
					style={{width: 280}}
					value={defaultTab}
					placeholder="Select default tab (Stock on Hand)"
					onChange={saveDefaultTab}
					options={getMaterialTabOptions()}
				/>
			),
		},
		{
			key: "default-equipment-tab",
			label: "Default Equipment Tab",
			children: (
				<Select
					style={{width: 280}}
					value={defaultEquipmentTab}
					placeholder="Select default tab (Bill of Material)"
					onChange={saveDefaultEquipmentTab}
					options={getEquipmentTabOptions()}
				/>
			),
		},
		{
			key: "default-operators",
			label: "Default Operators",
			children: (
				<Select
					mode="multiple"
					style={{width: "100%"}}
					value={defaultOperators}
					placeholder="Select default operators (All)"
					onChange={saveDefaultOperators}
					options={getOperatorOptions()}
					maxTagCount="responsive"
				/>
			),
		},
	];

	const collapseItems = [...tableItems, ...defaultItems];

	return (
		<Card data-testid="user-preferences-card">
			<Row>
				<Col span={24} className={styles.cardWrapper}>
					<Card>
                            <Button className={styles.resetButton} danger onClick={handleResetToDefaults} data-testid="user-preferences-reset-button">
                                Reset to defaults
                            </Button>
						Show on hover:
						<div className={styles.rowSwitch}>
							Full Description
							<Switch
								className={styles.descriptionSwitch}
								checked={switchState}
								onClick={() => {
									saveValue(!switchState);
								}}
								data-testid="user-preferences-switch"
							/>
							Short Description
						</div>
						<Collapse
							items={collapseItems}
							activeKey={activeKey}
							onChange={key => setActiveKey(key)}
							data-testid="user-preferences-collapse"
						/>
					</Card>
				</Col>
			</Row>
		</Card>
	);
};
